import { useQuery } from '@apollo/client/react'
import {
  DataTable,
  FailureNotice,
  FilterBar,
  FOCUS_RING,
  Icon,
  SearchField,
  Section,
  Select,
  buttonClasses,
  cn,
  useLanguage,
  usePageTitle,
  useTranslate,
  type DataTableColumn,
} from '@athena/ui'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import {
  ManagedUsersDocument,
  type ManagedUserFieldsFragment,
} from '../graphql/generated/operations.js'
import { AccountStatusTag, InvitationTag, RoleTag } from '../features/display/Tags.js'
import { ListMessage, PageControls } from '../features/management/ManagementSupport.js'
import { countOf, formatDate, sortRoles } from '../lib/display.js'
import {
  LIST_PAGE_SIZE,
  isFiltered,
  nextSearch,
  type AccountSearch,
  type SearchChange,
} from '../lib/list-search.js'

const ROLE_FILTER = { tutor: 'TUTOR', student: 'STUDENT', admin: 'ADMIN' } as const
const SORT = { email: 'EMAIL', name: 'NAME' } as const

/**
 * The account list: find an account, see what it is and what state it is in,
 * and open it.
 *
 * A table rather than a card per account. An account here is a name, an
 * address and three short facts, and a 105px bordered card around that made a
 * page of twenty-five accounts four screens tall. One row per account, the
 * facts in columns, and the name as the row's one link — everything else an
 * administrator can do to an account is on the account's own page.
 *
 * Search, filters, sort and page are the URL (`lib/list-search.ts`), so
 * opening an account and pressing Back returns to exactly this list.
 */
export function UsersScreen() {
  const t = useTranslate()
  usePageTitle(t('admin.navUsers'))
  const language = useLanguage()
  const search = useSearch({ from: '/$language/' })
  const navigate = useNavigate()
  const page = search.page ?? 1
  // What is typed, until Enter makes it the search. Follows the address when
  // it changes under the box — Back, or Clear filters — so the box always
  // shows the search the list is showing.
  const [draft, setDraft] = useState(search.q ?? '')
  useEffect(() => setDraft(search.q ?? ''), [search.q])

  const { data, previousData, loading, error, refetch } = useQuery(ManagedUsersDocument, {
    variables: {
      input: {
        query: search.q ?? '',
        offset: (page - 1) * LIST_PAGE_SIZE,
        limit: LIST_PAGE_SIZE,
        role: search.role ? ROLE_FILTER[search.role] : null,
        disabled: search.status ? search.status === 'disabled' : null,
        sort: search.sort ? SORT[search.sort] : 'NEWEST',
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })
  // The last page stays on screen while the next one loads, so narrowing the
  // list dims it for a moment instead of emptying it.
  const result = (data ?? previousData)?.adminUserPage
  const filtered = isFiltered(search)

  function go(change: SearchChange<AccountSearch>) {
    void navigate({
      to: '/$language',
      params: { language },
      search: nextSearch(search, change),
    })
  }

  const columns: DataTableColumn<ManagedUserFieldsFragment>[] = [
    {
      id: 'account',
      header: t('admin.columnAccount'),
      className: 'md:w-[40%]',
      cell: (user) => {
        const name = displayName(user)
        return (
          <>
            <Link
              to="/$language/accounts/$userId"
              params={{ language, userId: user.id }}
              className={cn(
                'rounded-sm font-semibold text-text underline-offset-2 hover:text-primary hover:underline',
                FOCUS_RING,
              )}
            >
              {name ?? user.email}
            </Link>
            {name ? <span className="block text-text-secondary">{user.email}</span> : null}
          </>
        )
      },
    },
    {
      id: 'profiles',
      header: t('admin.columnProfiles'),
      cell: (user) =>
        user.roles.length === 0 ? (
          <span className="text-text-secondary">{t('admin.noProfile')}</span>
        ) : (
          <span className="flex flex-wrap gap-1.5">
            {sortRoles(user.roles).map((role) => (
              <RoleTag key={role} role={role} />
            ))}
          </span>
        ),
    },
    {
      id: 'status',
      header: t('admin.columnStatus'),
      cell: (user) => (
        <span className="flex flex-wrap gap-1.5">
          <AccountStatusTag disabled={user.disabled} />
          {user.pendingInvitation ? <InvitationTag /> : null}
        </span>
      ),
    },
    {
      id: 'created',
      header: t('admin.columnCreated'),
      priority: 'secondary',
      className: 'whitespace-nowrap',
      cell: (user) => (
        <span className="text-text-secondary">{formatDate(user.createdAt, language)}</span>
      ),
    },
  ]

  return (
    <Section
      id="users"
      level={1}
      title={t('admin.usersTitle')}
      aside={
        <Link
          to="/$language/users/new"
          params={{ language }}
          className={cn(buttonClasses({ size: 'sm' }), 'gap-1.5 max-md:h-touch-target-min')}
        >
          <Icon.Plus aria-hidden="true" className="size-icon-sm" />
          {t('management.createUser')}
        </Link>
      }
    >
      <form
        role="search"
        aria-label={t('admin.usersTitle')}
        onSubmit={(event) => {
          event.preventDefault()
          const q = draft.trim()
          go({ q: q === '' ? undefined : q })
        }}
      >
        <FilterBar
          density="compact"
          clearLabel={t('common.clearFilters')}
          // Only when something narrows the list: a clear that does nothing
          // is a control that has to be read before it can be ignored.
          {...(filtered
            ? { onClear: () => go({ q: undefined, role: undefined, status: undefined }) }
            : {})}
          lead={
            <SearchField
              name="q"
              label={t('admin.searchLabel')}
              placeholder={t('admin.searchLabel')}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
          }
        >
          <Select
            label={t('admin.columnProfiles')}
            value={search.role ?? 'all'}
            onValueChange={(value) =>
              go({ role: value === 'all' ? undefined : (value as AccountSearch['role']) })
            }
            options={[
              { value: 'all', label: t('admin.allProfiles') },
              { value: 'tutor', label: t('admin.roleTutor') },
              { value: 'student', label: t('admin.roleStudent') },
              { value: 'admin', label: t('admin.roleAdmin') },
            ]}
          />
          <Select
            label={t('admin.columnStatus')}
            value={search.status ?? 'all'}
            onValueChange={(value) =>
              go({ status: value === 'all' ? undefined : (value as AccountSearch['status']) })
            }
            options={[
              { value: 'all', label: t('admin.allStatuses') },
              { value: 'active', label: t('admin.statusActive') },
              { value: 'disabled', label: t('admin.statusDisabled') },
            ]}
          />
          <Select
            label={t('management.sort')}
            value={search.sort ?? 'newest'}
            onValueChange={(value) =>
              go({ sort: value === 'newest' ? undefined : (value as AccountSearch['sort']) })
            }
            options={[
              { value: 'newest', label: t('management.newest') },
              { value: 'email', label: t('management.email') },
              { value: 'name', label: t('management.name') },
            ]}
          />
        </FilterBar>
      </form>

      {error ? (
        <FailureNotice
          title={t('admin.listLoadError')}
          error={error}
          retrying={loading}
          onRetry={() => void refetch()}
        />
      ) : null}

      {result ? (
        <p aria-live="polite" className="text-body-sm text-text-secondary">
          {countOf(t, result.total, 'admin.accountsCountOne', 'admin.accountsCountOther')}
        </p>
      ) : null}

      {result && result.total === 0 ? (
        <ListMessage
          action={
            filtered
              ? {
                  label: t('common.clearFilters'),
                  onClick: () => go({ q: undefined, role: undefined, status: undefined }),
                }
              : undefined
          }
        >
          {filtered ? t('admin.noUsers') : t('admin.noAccounts')}
        </ListMessage>
      ) : result || (loading && !error) ? (
        <DataTable
          caption={t('admin.usersTitle')}
          columns={columns}
          rows={result?.items ?? []}
          rowKey={(user) => user.id}
          rowTone={(user) => (user.disabled ? 'muted' : 'default')}
          busy={loading && Boolean(result)}
          loadingRows={8}
        />
      ) : null}

      {result ? (
        <PageControls
          page={page}
          total={result.total}
          limit={LIST_PAGE_SIZE}
          disabled={loading}
          onChange={(next) => go({ page: next })}
        />
      ) : null}
    </Section>
  )
}

/** The name the account goes by: its tutor name, its student name, or its own. */
function displayName(user: ManagedUserFieldsFragment): string | null {
  const student = [user.studentFirstName, user.studentLastName].filter(Boolean).join(' ')
  return user.tutorDisplayName || student || user.name || null
}
